import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Share2, Check, Smartphone } from "lucide-react";

/**
 * InstallAppButton
 *   - Listens to the `beforeinstallprompt` event and shows a stylish install pill
 *     when the browser is offering PWA installation.
 *   - Hides itself entirely if:
 *       • already installed (display-mode: standalone)
 *   - Always shows a small "How to install on iPhone/Android" link that opens
 *     the visual `/install` guide — works on every device, even iOS Safari which
 *     never fires `beforeinstallprompt`.
 *
 * Props:
 *   variant: "login" | "dashboard" — slight styling difference
 */
export default function InstallAppButton({ variant = "login" }) {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const isStandalone =
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (isStandalone) {
            setInstalled(true);
            return;
        }

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferred(null);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    if (installed) return null;

    const onInstallClick = async () => {
        if (!deferred) return;
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") setInstalled(true);
        setDeferred(null);
    };

    const onShareClick = async () => {
        const url = `${window.location.origin}/install`;
        const msg = `Install "Tax Solution" on your phone — easy step-by-step guide: ${url}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "Install Tax Solution", text: msg, url });
            } else {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2200);
            }
        } catch {
            /* user cancelled share — silently ignore */
        }
    };

    const wrapClass =
        variant === "dashboard"
            ? "flex flex-wrap items-center gap-2"
            : "flex flex-wrap items-center justify-center gap-2 mt-4";

    return (
        <div className={wrapClass} data-testid="install-app-wrap">
            {deferred && (
                <button
                    type="button"
                    onClick={onInstallClick}
                    className="inline-flex items-center gap-2 bg-[#0A1128] text-[#C5A059] px-4 py-2 text-xs uppercase tracking-wider font-semibold border border-[#C5A059] hover:bg-[#C5A059] hover:text-[#0A1128] transition-colors"
                    data-testid="install-app-btn"
                >
                    <Download size={14} />
                    Install Tax Solution App
                </button>
            )}
            <Link
                to="/install"
                data-testid="install-howto-link"
                className="inline-flex items-center gap-2 bg-white text-[#0A1128] px-3 py-2 text-[11px] uppercase tracking-wider font-medium border border-[#E1E4EB] hover:bg-[#FAF3E0] hover:border-[#C5A059] transition-colors"
                title="Step-by-step install guide for iPhone & Android"
            >
                <Smartphone size={13} />
                How to install on iPhone / Android
            </Link>
            <button
                type="button"
                onClick={onShareClick}
                className="inline-flex items-center gap-2 bg-white text-[#0A1128] px-3 py-2 text-[11px] uppercase tracking-wider font-medium border border-[#E1E4EB] hover:bg-[#FAF3E0] hover:border-[#C5A059] transition-colors"
                title="Copy install link to share with clients"
                data-testid="install-share-btn"
            >
                {copied ? <Check size={13} /> : <Share2 size={13} />}
                {copied ? "Link copied!" : "Share install link"}
            </button>
        </div>
    );
}
